/**
 * LocalStorage service for persisting player state
 */

const STORAGE_VERSION = 1;
const DEBOUNCE_MS = 500;
const POSITION_SAVE_INTERVAL_MS = 10000;

/**
 * Base interface for stored data
 */
export interface StoredData<T> {
    version: number;
    key: string;
    pageUrl: string;
    lastModified: number;
    state: T;
}

/**
 * Generate storage key for an item on a specific page
 */
export function getStorageKey(id: string, prefix: string): string {
    const pathname = encodeURIComponent(window.location.pathname);
    return `${prefix}:${id}:${pathname}`;
}

/**
 * Save state to localStorage
 */
export function saveState<T>(id: string, state: T, prefix: string): void {
    const key = getStorageKey(id, prefix);
    const data: StoredData<T> = {
        version: STORAGE_VERSION,
        key: id,
        pageUrl: window.location.pathname,
        lastModified: Date.now(),
        state,
    };

    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
        console.error(`[Storage] Failed to save state for ${prefix}:`, error);
    }
}

/**
 * Load state from localStorage
 */
export function loadState<T>(id: string, prefix: string): T | null {
    const key = getStorageKey(id, prefix);

    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const data: StoredData<T> = JSON.parse(raw);

        // Version check for future migrations
        if (data.version !== STORAGE_VERSION) {
            console.warn(`[Storage] Version mismatch for ${prefix}, ignoring saved state`);
            return null;
        }

        return data.state;
    } catch (error) {
        console.error(`[Storage] Failed to load state for ${prefix}:`, error);
        return null;
    }
}

/**
 * Delete state from localStorage
 */
export function deleteState(id: string, prefix: string): void {
    const key = getStorageKey(id, prefix);
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error(`[Storage] Failed to delete state for ${prefix}:`, error);
    }
}

/**
 * Create a debounced save function
 */
export function createDebouncedSave<T>(
    id: string,
    prefix: string
): (state: T) => void {
    let timeoutId: number | null = null;

    return (state: T) => {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        timeoutId = window.setTimeout(() => {
            saveState(id, state, prefix);
            timeoutId = null;
        }, DEBOUNCE_MS);
    };
}

/**
 * Create a position saver that runs periodically
 */
export function createPeriodicSaver<T>(
    id: string,
    getState: () => T,
    prefix: string
): { start: () => void; stop: () => void } {
    let intervalId: number | null = null;

    return {
        start() {
            if (intervalId !== null) return;
            intervalId = window.setInterval(() => {
                saveState(id, getState(), prefix);
            }, POSITION_SAVE_INTERVAL_MS);
        },
        stop() {
            if (intervalId !== null) {
                clearInterval(intervalId);
                intervalId = null;
            }
        },
    };
}
